import { useState, useCallback } from 'react';
import api from '../services/api';
import toastUtils from '../utils/toastUtils';

const useKanbanSubtasks = (taskId) => {
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSubtasks = useCallback(async () => {
    if (!taskId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/kanban-tasks/${taskId}/subtasks`);
      setSubtasks(response.data);
    } catch (err) {
      console.error('Error fetching subtasks:', err);
      setError(err);
      toastUtils.error('Erro ao carregar subtarefas', 'Erro no Kanban');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const createSubtask = useCallback(async (subtaskData) => {
    try {
      setLoading(true);
      
      const response = await api.post('/kanban-tasks', {
        ...subtaskData,
        parentTaskId: taskId
      });
      
      setSubtasks(prev => [...prev, response.data]);
      toastUtils.success('Subtarefa criada com sucesso', 'Task Criada');
      return response.data;
    } catch (err) {
      console.error('Error creating subtask:', err);
      toastUtils.error('Erro ao criar subtarefa', 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const updateSubtask = useCallback(async (subtaskId, updates) => {
    try {
      const response = await api.put(`/kanban-tasks/${subtaskId}`, updates);
      
      setSubtasks(prev => 
        prev.map(subtask => 
          subtask.id === subtaskId ? { ...subtask, ...response.data } : subtask
        )
      );
      
      return response.data;
    } catch (err) {
      console.error('Error updating subtask:', err);
      toastUtils.error('Erro ao atualizar subtarefa', 'Erro no Kanban');
      throw err;
    }
  }, []);

  const deleteSubtask = useCallback(async (subtaskId) => {
    try {
      await api.delete(`/kanban-tasks/${subtaskId}`);
      
      setSubtasks(prev => prev.filter(subtask => subtask.id !== subtaskId));
      toastUtils.success('Subtarefa removida com sucesso', 'Status Alterado');
    } catch (err) {
      console.error('Error deleting subtask:', err);
      toastUtils.error('Erro ao remover subtarefa', 'Erro no Kanban');
      throw err;
    }
  }, []);

  const toggleSubtaskCompletion = useCallback(async (subtaskId, completed) => {
    try {
      const response = await api.put(`/kanban-tasks/${subtaskId}`, {
        status: completed ? 'done' : 'todo'
      });
      
      setSubtasks(prev => 
        prev.map(subtask => 
          subtask.id === subtaskId 
            ? { ...subtask, status: response.data.status, completed } 
            : subtask
        )
      );
      
      return response.data;
    } catch (err) {
      console.error('Error toggling subtask completion:', err);
      toastUtils.error('Erro ao atualizar status da subtarefa', 'Erro no Kanban');
      throw err;
    }
  }, []);

  return {
    subtasks,
    loading,
    error,
    fetchSubtasks,
    createSubtask,
    updateSubtask,
    deleteSubtask,
    toggleSubtaskCompletion,
  };
};

export default useKanbanSubtasks;